import { Alert } from '@ksyos/design-system';
import { some } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { BezoekAanSpecialistOption } from '../../../../../../common/enums/bezoekAanSpecialistOption';
import { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { YesOrNo } from '../../../../../../common/enums/yesOrNo';
import {
    calculateAbcdDermatoscopie,
    getAbcdDermatoscopiescoreByTotal,
} from '../../../../../../common/utils/abcdDermatoscopie';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import {
    DermatologyPolicies,
    DermatologyUrgencies,
} from '../../../../../../common/validation/customControls/dermatology';
import { nullDiagnosis } from '../../../../../../common/validation/diagnosis';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    AnchorLink,
    ButtonContainer,
    Form,
    FormDivider,
    FormParagraph,
    HeaderDivider,
    Icon,
    IconColor,
    InfoTooltip,
    InlineElement,
    InputCheckbox,
} from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { DropdownField } from '../../../../fields/Dropdown';
import { NumberInput } from '../../../../fields/Number';
import { RadioGroupField, RadioGroupInput } from '../../../../fields/RadioGroup';
import { TextAreaField } from '../../../../fields/TextArea';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { addFormAnalytics, addFormConversion, addTrackFormSubmit } from '../../utils/formAnalytics';
import { Icd10Selections } from '../../utils/v1/Icd10/Icd10Selections';

export interface IDermaCscV1DiagnosticReportAddEditCustomControlData {
    asymmetrie: number | null;
    beleid: DermatologyPolicies | null;
    beleid_anders: string | null;
    bevindingen: string | null;
    bezoek_aan_specialist_nodig: BezoekAanSpecialistOption | null;
    border: number | null;
    color: number | null;
    conclusie_diagnose: ConclusieDiagnoseOption;
    differentialStructures: number | null;
    kwaliteit_fotos_voldoende: YesOrNo | null;
    omschrijving_beleid: string | null;
    toelichting: string | null;
    urgentie: DermatologyUrgencies | null;
    werkdiagnose: {
        anders: string | null;
        atypische_naevus: boolean;
        naevus_naevocellularis: boolean;
        verdenking_melanoom: boolean;
    };
}

const policyOptions = [
    DermatologyPolicies.expectatiefBeleid,
    DermatologyPolicies.verwijzingNaarDermatoloog,
    DermatologyPolicies.bioptDoorHuisarts,
    DermatologyPolicies.anders,
];

const policyOptionsZieBeleid = [
    DermatologyPolicies.verwijzingNaarDermatoloog,
    DermatologyPolicies.bioptDoorHuisarts,
    DermatologyPolicies.anders,
];

const bezoekOptions = [
    BezoekAanSpecialistOption.ja,
    BezoekAanSpecialistOption.nee,
    BezoekAanSpecialistOption.zieBeleid,
];

const urgencyOptions = [
    DermatologyUrgencies.sixMonths,
    DermatologyUrgencies.threeMonths,
    DermatologyUrgencies.oneMonth,
    DermatologyUrgencies.oneWeek,
    DermatologyUrgencies.oneDay,
];

const conclusieDiagnoseOptions = [
    ConclusieDiagnoseOption.ja,
    ConclusieDiagnoseOption.nog_geen_diagnose,
    ConclusieDiagnoseOption.geen_afwijkingen,
];

export const abcdDermatoscopiescoreTooltipText = (
    <>
        <ul>
            <li className="listheader">Asymmetrie - Score 0 tm 2</li>
            <li className="no-style">Asymmetrie in 0, 1 of 2 assen</li>
            <li className="no-style">Asymmetrie qua kleur, textuur, of vorm</li>
        </ul>
        <ul>
            <li className="listheader">Border (Rand) - Score 0 tm 8</li>
            <li className="no-style">
                Abrupte, scherp begrensde overgang van pigmentpatroon in 0 - 8 segmenten van de laesie beoordeeld
            </li>
        </ul>
        <ul>
            <li className="listheader">Color (Kleur) - Score 0 tm 6</li>
            <li className="no-style">
                Aanwezigheid van 1 tot 6 specifieke kleuren: wit, rood, lichtbruin, donkerbruin, grijsblauw en zwart
            </li>
        </ul>
        <ul>
            <li className="listheader">Differential structures (verschillende structuren) - Score 0 tm 5</li>
            <li className="no-style">De specifieke aanwezigheid van 0 tot 5 bijzondere structuren:</li>
            <li>Network (abnormaal verbreed pigment netwerk)</li>
            <li>Structureless area (structuurloze gebieden)</li>
            <li>Dots (kleine zwarte of bruine puntjes)</li>
            <li>Globules (bruinzwarte ronde pigmentvlekjes)</li>
            <li>Branched streaks (vertakte uitlopertjes naar de periferie)</li>
        </ul>
    </>
);

export function DermaCscV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
    const [werkdiagnoseAndersChecked, setWerkdiagnoseAndersChecked] = useState<boolean>(
        props.diagnosticReport.customControlData.werkdiagnose.anders !== null
    );

    const customControlData: IDermaCscV1DiagnosticReportAddEditCustomControlData =
        props.diagnosticReport.customControlData;

    useEffect(() => {
        setWerkdiagnoseAndersChecked(
            props.diagnosticReport.diagnosis.diagnosisConsiderationIcd10 !== null ||
                props.diagnosticReport.diagnosis.considerationThesaurusTermId !== null
        );
    }, [props.diagnosticReport.diagnosis]);

    const formRef = useRef<HTMLFormElement>(null);
    const saveSendButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        props.handleChangeCustomControl({
            ...dermaCscV1DiagnosticReportInitializeCustomControlData(),
            ...props.diagnosticReport.customControlData,
        });
        addFormAnalytics(formRef.current, saveSendButtonRef.current);
    }, []);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process ? props.process.dateSent : undefined}
                isFirstItem={props.isFirstItem}
                isItemOpen
                organizationName={
                    props.currentDiagnosticReport?.performerOrganization?.name
                        ? props.currentDiagnosticReport?.performerOrganization?.name
                        : undefined
                }
                practitionerName={
                    props.currentDiagnosticReport?.performerPractitioner
                        ? getDisplayName(props.currentDiagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <Form
                formRef={formRef}
                id="DermaCscV1DiagnosticReportAddEditId"
                name="DermaCscV1DiagnosticReportAddEdit"
                onSubmit={() => null}
            >
                {showForm()}

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.diagnosticReport}
                            isExisting={!!props.request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveDiagnosticReport('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveDiagnosticReport('final');
                            }}
                            saveSendButtonRef={saveSendButtonRef}
                        />,
                    ]}
                />
            </Form>
        </ProcessContainer>
    );

    function showForm() {
        const total = calculateAbcdDermatoscopie(customControlData);

        return (
            <>
                <FormContextProvider
                    value={{
                        placeholderGroupName: 'diagnosticReport',
                        item: customControlData,
                        errors: props.errors,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <TextAreaField
                        error={props.errors.get('customControlData.bevindingen')}
                        fieldKey="bevindingen"
                        hasErrors={props.errors.has('customControlData.bevindingen')}
                        isRequired
                        label={translateKey('diagnosticReport.findings')}
                        placeholder="Omschrijf je bevindingen..."
                    />

                    <RadioGroupField
                        error={props.errors.get('customControlData.kwaliteit_fotos_voldoende')}
                        fieldKey="kwaliteit_fotos_voldoende"
                        hasErrors={props.errors.has('customControlData.kwaliteit_fotos_voldoende')}
                        label={translateKey('diagnosticReport.qualityPhotosSufficient')}
                        labels={[translateKey('common.yes'), translateKey('common.no')]}
                        name="kwaliteit_fotos_voldoende"
                        options={[YesOrNo.yes, YesOrNo.no]}
                    />

                    <HorizontalFormGroup hasErrors={props.errors.has('customControlData.conclusie_diagnose')}>
                        <FormLabel isRequired>Heb je een werkdiagnose?</FormLabel>
                        <InputContainer>
                            <RadioGroupInput
                                error={props.errors.get('customControlData.conclusie_diagnose')}
                                fieldKey="conclusie_diagnose"
                                labels={conclusieDiagnoseOptions}
                                onChange={async (value) => {
                                    await props.handleChangeCustomControl({ conclusie_diagnose: value });
                                    if (value !== ConclusieDiagnoseOption.ja) {
                                        await clearDiagnosis();
                                        await props.handleChangeCustomControl({
                                            werkdiagnose: {
                                                atypische_naevus: false,
                                                naevus_naevocellularis: false,
                                                verdenking_melanoom: false,
                                                anders: null,
                                            },
                                        });
                                        setWerkdiagnoseAndersChecked(false);
                                    }
                                }}
                                options={conclusieDiagnoseOptions}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    {customControlData.conclusie_diagnose === ConclusieDiagnoseOption.ja && (
                        <HorizontalFormGroup hasErrors={props.errors.has('customControlData.werkdiagnose')}>
                            <FormLabel isRequired>{translateKey('diagnosticReport.workingDiagnosis')}</FormLabel>
                            <InputContainer>
                                <InputCheckbox
                                    checked={customControlData.werkdiagnose.atypische_naevus}
                                    name="Atypische naevus"
                                    onChange={async (value) =>
                                        await props.handleChangeCustomControl({
                                            werkdiagnose: {
                                                ...customControlData.werkdiagnose,
                                                atypische_naevus: value,
                                            },
                                        })
                                    }
                                    onDoValidate={async () => await props.validateOneCustomControl('werkdiagnose')}
                                    text="Atypische naevus"
                                />

                                <InputCheckbox
                                    checked={customControlData.werkdiagnose.naevus_naevocellularis}
                                    name="Naevus naevocellularis"
                                    onChange={async (value) =>
                                        await props.handleChangeCustomControl({
                                            werkdiagnose: {
                                                ...customControlData.werkdiagnose,
                                                naevus_naevocellularis: value,
                                            },
                                        })
                                    }
                                    onDoValidate={async () => await props.validateOneCustomControl('werkdiagnose')}
                                    text="Naevus naevocellularis"
                                />

                                <InputCheckbox
                                    checked={customControlData.werkdiagnose.verdenking_melanoom}
                                    name="Verdenking melanoom"
                                    onChange={async (value) =>
                                        await props.handleChangeCustomControl({
                                            werkdiagnose: {
                                                ...customControlData.werkdiagnose,
                                                verdenking_melanoom: value,
                                            },
                                        })
                                    }
                                    onDoValidate={async () => await props.validateOneCustomControl('werkdiagnose')}
                                    text="Verdenking melanoom"
                                />
                                <HorizontalFormGroup noMargin>
                                    <InputCheckbox
                                        checked={werkdiagnoseAndersChecked}
                                        checkOnTextClickedDisabled
                                        onChange={async (value) => {
                                            if (!value) {
                                                await props.handleChangeCustomControl({
                                                    werkdiagnose: { ...customControlData.werkdiagnose, anders: null },
                                                });
                                                await clearDiagnosis();
                                            }
                                            setWerkdiagnoseAndersChecked(value);
                                        }}
                                        onDoValidate={async () => {
                                            await props.validateOne('diagnosis');
                                            await props.validateOneCustomControl('werkdiagnose');
                                        }}
                                    />
                                    <InputContainer className="pr-0">
                                        <Icd10Selections
                                            bold
                                            diagnosis={props.diagnosticReport.diagnosis}
                                            errors={props.errors}
                                            hideWorkingDiagnosisLabel
                                            isRequired
                                            isWorkingDiagnosis={false}
                                            onChange={async (diagnosis) => {
                                                await props.handleChange('diagnosis', diagnosis);
                                                await props.handleChangeCustomControl({
                                                    werkdiagnose: {
                                                        ...customControlData.werkdiagnose,
                                                        anders:
                                                            diagnosis.diagnosisConsiderationIcd10 ||
                                                            diagnosis.diagnosisConsiderationIcd10fallback ||
                                                            null,
                                                    },
                                                });
                                            }}
                                            onDoValidate={async () => {
                                                await props.validateOne('diagnosis');
                                                await props.validateOneCustomControl('werkdiagnose');
                                            }}
                                            placeholder="Beschrijf of klik en kies uit de lijst"
                                            showOnlyWorkingDiagnosis
                                            specialismCode={props.service.specialismCode || ''}
                                        />
                                    </InputContainer>
                                </HorizontalFormGroup>
                            </InputContainer>
                        </HorizontalFormGroup>
                    )}

                    {shouldShowIcd10Selections() && (
                        <Icd10Selections
                            bold
                            diagnosis={props.diagnosticReport.diagnosis}
                            errors={props.errors}
                            hideWorkingDiagnosis
                            isRequired
                            isWorkingDiagnosis={false}
                            onChange={async (diagnosis) => await props.handleChange('diagnosis', diagnosis)}
                            onDoValidate={async () => await props.validateOne('diagnosis')}
                            placeholder="Beschrijf of klik en kies uit de lijst"
                            specialismCode={props.service.specialismCode || ''}
                        />
                    )}

                    <FormDivider />

                    <HorizontalFormGroup>
                        <FormLabel className="header">
                            <InlineElement className="d-inline-block">ABCD</InlineElement>{' '}
                            <InlineElement className="d-inline-block">
                                Dermatoscopie
                                <InfoTooltip text={abcdDermatoscopiescoreTooltipText} />
                            </InlineElement>
                        </FormLabel>
                        <div className="pl-2 bold col-sm-9">Score</div>
                        <div className="pl-0 text-align-right col-sm-9">
                            <AnchorLink
                                openInNewTab
                                paddingDirection="l"
                                paddingValue={2}
                                to="https://dermoscopedia.org/ABCD_rule"
                            >
                                <Icon
                                    color={IconColor.secondary}
                                    icon="external"
                                />
                                Meer over de dermatoscopie
                            </AnchorLink>
                        </div>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={props.errors.has('customControlData.asymmetrie')}>
                        <FormLabel>Asymmetrie</FormLabel>
                        <InputContainer size="medium">
                            <NumberInput
                                error={props.errors.get('customControlData.asymmetrie')}
                                fieldKey="asymmetrie"
                                placeholder="0-2"
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={props.errors.has('customControlData.border')}>
                        <FormLabel>Border (Rand)</FormLabel>
                        <InputContainer size="medium">
                            <NumberInput
                                error={props.errors.get('customControlData.border')}
                                fieldKey="border"
                                placeholder="0-8"
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={props.errors.has('customControlData.color')}>
                        <FormLabel>Color (Kleur)</FormLabel>
                        <InputContainer size="medium">
                            <NumberInput
                                error={props.errors.get('customControlData.color')}
                                fieldKey="color"
                                placeholder="0-6"
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={props.errors.has('customControlData.differentialStructures')}>
                        <FormLabel>Differential Structures</FormLabel>
                        <InputContainer size="medium">
                            <NumberInput
                                error={props.errors.get('customControlData.differentialStructures')}
                                fieldKey="differentialStructures"
                                placeholder="0-5"
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroupRead>
                        <FormLabel> Totaal </FormLabel>
                        <InputContainer>
                            {total !== null && (
                                <>
                                    <FormParagraph bold>{total.toFixed(2)}</FormParagraph>
                                    <FormParagraph bold>{getAbcdDermatoscopiescoreByTotal(total)}.</FormParagraph>
                                </>
                            )}
                        </InputContainer>
                    </HorizontalFormGroupRead>

                    <FormDivider />
                    <HeaderDivider text={translateKey('diagnosticReport.advice')} />

                    <TextAreaField
                        error={props.errors.get('customControlData.omschrijving_beleid')}
                        fieldKey="omschrijving_beleid"
                        hasErrors={props.errors.has('customControlData.omschrijving_beleid')}
                        label={translateKey('diagnosticReport.policyDescription')}
                        placeholder="Een toelichting, opmerkingen of vragen..."
                    />

                    <DropdownField
                        error={props.errors.get('customControlData.beleid')}
                        fieldKey="beleid"
                        hasErrors={props.errors.has('customControlData.beleid')}
                        label={translateKey('diagnosticReport.orChoosePolicyFromList')}
                        onChange={async (value) => {
                            const update: Partial<IDermaCscV1DiagnosticReportAddEditCustomControlData> = {
                                beleid: value,
                            };

                            if (update.beleid_anders && value !== DermatologyPolicies.anders) {
                                update.beleid_anders = null;
                            }

                            if (
                                value === DermatologyPolicies.expectatiefBeleid ||
                                customControlData.bezoek_aan_specialist_nodig !== BezoekAanSpecialistOption.ja
                            ) {
                                update.urgentie = null;
                            }

                            if (
                                value === DermatologyPolicies.expectatiefBeleid ||
                                value === DermatologyPolicies.verwijzingNaarDermatoloog
                            ) {
                                update.bezoek_aan_specialist_nodig = BezoekAanSpecialistOption.ja;
                            }

                            await props.handleChangeCustomControl(update);
                        }}
                        placeholder="Kies het te voeren beleid..."
                        selectOptions={(customControlData.bezoek_aan_specialist_nodig ===
                        BezoekAanSpecialistOption.zieBeleid
                            ? policyOptionsZieBeleid
                            : policyOptions
                        ).map((option) => {
                            return { value: option, text: option };
                        })}
                    />

                    {customControlData.beleid === DermatologyPolicies.anders && (
                        <TextAreaField
                            error={props.errors.get('customControlData.beleid_anders')}
                            fieldKey="beleid_anders"
                            hasErrors={props.errors.has('customControlData.beleid_anders')}
                            label=" "
                            placeholder="Toelichting..."
                        />
                    )}

                    <RadioGroupField
                        error={props.errors.get('customControlData.bezoek_aan_specialist_nodig')}
                        fieldKey="bezoek_aan_specialist_nodig"
                        hasErrors={props.errors.has('customControlData.bezoek_aan_specialist_nodig')}
                        isRequired
                        label={translateKey('diagnosticReport.visitToSpecialistNecessaryNow')}
                        name="bezoek_aan_specialist_nodig"
                        onChange={async (value) => {
                            const update: Partial<IDermaCscV1DiagnosticReportAddEditCustomControlData> = {
                                bezoek_aan_specialist_nodig: value,
                            };

                            if (
                                value === BezoekAanSpecialistOption.zieBeleid &&
                                customControlData.beleid === DermatologyPolicies.expectatiefBeleid
                            ) {
                                update.beleid = null;
                            }

                            if (
                                value !== BezoekAanSpecialistOption.ja ||
                                customControlData.beleid === DermatologyPolicies.expectatiefBeleid
                            ) {
                                update.urgentie = null;
                            }

                            await props.handleChangeCustomControl(update);
                        }}
                        options={bezoekOptions}
                    />

                    {customControlData.bezoek_aan_specialist_nodig === BezoekAanSpecialistOption.ja &&
                        customControlData.beleid !== DermatologyPolicies.expectatiefBeleid && (
                            <DropdownField
                                error={props.errors.get('customControlData.urgentie')}
                                fieldKey="urgentie"
                                hasErrors={props.errors.has('customControlData.urgentie')}
                                label={translateKey('diagnosticReport.urgency')}
                                placeholder="Kies de mate van urgentie..."
                                selectOptions={urgencyOptions.map((option) => {
                                    return { value: option, text: option };
                                })}
                            />
                        )}

                    <TextAreaField
                        error={props.errors.get('customControlData.toelichting')}
                        fieldKey="toelichting"
                        hasErrors={props.errors.has('customControlData.toelichting')}
                        label={translateKey('diagnosticReport.remarks')}
                        placeholder="Een toelichting, opmerkingen of vragen..."
                    />
                    {customControlData.urgentie === DermatologyUrgencies.oneDay && (
                        <Alert appearance="info">
                            Let op: Neem bij spoed altijd contact op met Ksyos via 020 600 00 60 dan pakken wij de
                            spoedverwijzing op
                        </Alert>
                    )}
                </FormContextProvider>
            </>
        );
    }

    async function saveDiagnosticReport(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        addTrackFormSubmit(saveSendButtonRef.current);

        const validatedDiagnosticReport = await props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            setButtonDisabled(false);

            return;
        }

        const diagnosticReportId = await props.onSaveDiagnosticReport(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'diagnosticReport',
                translateKey('toast.draftDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'diagnosticReport',
                translateKey('toast.answerSentToOrganizationSuccess', props.request.requesterOrganization?.name)
            );
            addFormConversion(formRef.current);
        }

        setButtonDisabled(false);
        await props.onSave(diagnosticReportId);
    }

    async function clearDiagnosis() {
        await props.handleChange('diagnosis', {
            ...nullDiagnosis,
        });
    }

    function shouldShowIcd10Selections() {
        if (props.diagnosticReport.diagnosis.diagnosisConsiderationIcd10 !== null) {
            return true;
        }

        if (some(customControlData.werkdiagnose)) {
            return true;
        }

        return false;
    }
}

export function dermaCscV1DiagnosticReportInitializeCustomControlData() {
    return {
        asymmetrie: null,
        border: null,
        color: null,
        differentialStructures: null,
        bevindingen: null,
        kwaliteit_fotos_voldoende: null,
        conclusie_diagnose: ConclusieDiagnoseOption.ja,
        werkdiagnose: {
            atypische_naevus: false,
            naevus_naevocellularis: false,
            verdenking_melanoom: false,
            anders: null,
        },
        omschrijving_beleid: null,
        beleid: null,
        beleid_anders: null,
        bezoek_aan_specialist_nodig: null,
        urgentie: null,
        toelichting: null,
    };
}

export function dermaCscV1DiagnosticReportValidateOneCustomControlData(
    name: string,
    customControlData: any,
    errors: ValidationErrors
) {
    if (name === 'werkdiagnose') {
        return validateErrorsForWerkdiagnose(customControlData, errors);
    }

    return errors;
}

export function dermaCscV1DiagnosticReportValidateCustomControlData(customControlData: any, errors: ValidationErrors) {
    errors = validateErrorsForWerkdiagnose(customControlData, errors);

    return errors;
}

function validateErrorsForWerkdiagnose(
    customControlData: IDermaCscV1DiagnosticReportAddEditCustomControlData,
    errors: ValidationErrors
) {
    let validationErrors = errors;

    if (customControlData.conclusie_diagnose !== ConclusieDiagnoseOption.ja) {
        return validationErrors.remove('customControlData.werkdiagnose');
    }

    const isWerkdiagnoseFilled =
        customControlData.werkdiagnose.atypische_naevus ||
        customControlData.werkdiagnose.naevus_naevocellularis ||
        customControlData.werkdiagnose.verdenking_melanoom ||
        customControlData.werkdiagnose.anders !== null;

    if (!isWerkdiagnoseFilled) {
        validationErrors = validationErrors.set('customControlData.werkdiagnose', 'any_required');
    } else {
        validationErrors = validationErrors.remove('customControlData.werkdiagnose');
    }

    return validationErrors;
}
